import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Info, X, Grid, List, Flame, MessageCircle } from 'lucide-react';

const Catalog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await fetch('/products.json');
        if (response.ok) {
          const data = await response.json();
          const transformedProducts = data.flatMap((item, index) => {
            if (item.product_name === 'Others' && item.Items) {
              return item.Items.map((itemName, itemIndex) => ({
                id: `others-${itemIndex}`,
                name: itemName,
                category: 'others',
                image: null,
                description: `Fire safety equipment conforming to Indian Standards`,
                technical_specifications: {},
                isStandardItem: true
              }));
            } else {
              return {
                id: index,
                name: item.product_name,
                category: categorizeProduct(item.product_name),
                image: item.image ? `/products/${item.image}` : null,
                description: generateDescription(item.product_name),
                technical_specifications: item.technical_specifications || {},
                isStandardItem: false
              };
            }
          });
          setProducts(transformedProducts);
        } else {
          setProducts([]);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error loading products:', error);
        setLoading(false);
      }
    };
    loadProducts();
  }, []);

  const categorizeProduct = (productName) => {
    const name = productName.toLowerCase();
    if (name.includes('extinguisher') || name.includes('fire ball')) return 'extinguishers';
    if (name.includes('call point') || name.includes('hooter') || name.includes('alarm')) return 'detection';
    if (name.includes('sprinkler') || name.includes('hose')) return 'sprinklers';
    if (name.includes('valve') || name.includes('connection') || name.includes('cabinet') || name.includes('reel') || name.includes('drum')) return 'suppression';
    return 'others';
  };

  const generateDescription = (productName) => {
    const descriptions = {
      'Fire Extinguisher Ball': 'Automatic fire suppression ball that activates when exposed to flames',
      'Manual Call Point': 'Emergency fire alarm activation device for manual triggering',
      'Hooters': 'Audio alarm device for fire emergency notifications',
      'Flexible Sprinkler Hose Fitting': 'Flexible connection for sprinkler systems',
      'Automatic Sprinklers': 'Temperature-activated fire suppression sprinkler heads',
      'First Aid Hose Reel Drums': 'Manual fire fighting hose reel system',
      'RRL Hoses': 'Rubber reinforced lined fire fighting hoses',
      'Landing Valves': 'Fire hydrant outlet valves for hose connections',
      'Trolly Mounted Fire Extinguisher': 'Mobile fire extinguisher system for large areas',
      'Fire Brigade Inlet Connections': 'External connection point for fire brigade water supply',
      'Fire Hose Cabinet': 'Storage cabinet for fire fighting equipment'
    };
    for (const [key, desc] of Object.entries(descriptions)) {
      if (productName.includes(key)) return desc;
    }
    return 'Professional fire safety equipment meeting Indian Standards';
  };

  const categories = [
    { value: 'all', label: 'All Products' },
    { value: 'sprinklers', label: 'Sprinklers' },
    { value: 'extinguishers', label: 'Extinguishers' },
    { value: 'detection', label: 'Detection' },
    { value: 'suppression', label: 'Suppression' },
    { value: 'others', label: 'Others' }
  ];

  const filteredAndSortedProducts = useMemo(() => {
    let filtered = products.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            product.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
    filtered.sort((a, b) => a.name.localeCompare(b.name));
    return filtered;
  }, [products, searchTerm, selectedCategory]);

  const ProductCard = ({ product, index }) => {
    const hasSpecs = product.technical_specifications && Object.keys(product.technical_specifications).length > 0;
    
    if (viewMode === 'list') {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: index * 0.05 }}
          className="group relative flex flex-col sm:flex-row bg-[#1A1A2E] rounded-2xl overflow-hidden border border-[rgba(192,57,43,0.2)] hover:shadow-[0_8px_32px_rgba(192,57,43,0.3)] transition-all duration-300"
        >
          {/* Top Border Accent (Left side on desktop list view) */}
          <div className="absolute top-0 left-0 w-full sm:w-1 h-1 sm:h-full bg-primary transform scale-0 group-hover:scale-100 transition-transform origin-left sm:origin-top duration-300 z-10" />

          {/* Image Area */}
          <div className="w-full sm:w-[120px] sm:h-[120px] aspect-[4/3] sm:aspect-square bg-[#111827] flex-shrink-0 relative flex items-center justify-center p-4">
            <span className="absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] uppercase font-bold bg-primary text-white z-10">
              {product.category}
            </span>
            {product.image ? (
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className="w-full h-full flex items-center justify-center bg-[rgba(192,57,43,0.08)] rounded-lg"
              style={{ display: product.image ? 'none' : 'flex' }}
            >
              <Flame className="w-8 h-8 text-primary opacity-80" />
            </div>
          </div>

          {/* Info Area */}
          <div className="p-4 flex-1 flex flex-col sm:flex-row justify-between sm:items-center gap-4">
            <div>
              <h3 className="text-white font-bold text-[15px] mb-1 line-clamp-2">
                {product.name}
              </h3>
              <p className="text-[#9CA3AF] text-[13px] line-clamp-2 mb-2">
                {product.description}
              </p>
              {product.isStandardItem && (
                <span className="inline-block px-2 py-0.5 rounded-full bg-gray-700 text-gray-300 text-[10px]">
                  IS:14609:1999
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              {hasSpecs && (
                <button
                  onClick={() => setSelectedProduct(product)}
                  className="w-8 h-8 rounded-full border border-primary text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                >
                  <Info className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => setSelectedProduct(product)}
                className="px-4 py-2 rounded-lg border border-primary text-primary hover:bg-primary hover:text-white transition-colors text-sm font-semibold"
              >
                View Details
              </button>
            </div>
          </div>
        </motion.div>
      );
    }

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: index * 0.05 }}
        className="group relative flex flex-col bg-[#1A1A2E] rounded-[16px] overflow-hidden border border-[rgba(192,57,43,0.2)] hover:shadow-[0_8px_32px_rgba(192,57,43,0.3)] transition-all duration-300 h-full"
      >
        {/* Top Border Accent */}
        <div className="absolute top-0 left-0 w-full h-[3px] bg-primary transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300 z-10" />

        {/* Image Area */}
        <div className="relative aspect-[4/3] bg-[#111827] w-full flex items-center justify-center p-4">
          <span className="absolute top-3 left-3 px-2 py-0.5 rounded text-[10px] uppercase font-bold bg-primary text-white z-10">
            {product.category}
          </span>
          {product.image ? (
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className="w-full h-full flex items-center justify-center bg-[rgba(192,57,43,0.08)] rounded-xl"
            style={{ display: product.image ? 'none' : 'flex' }}
          >
            <Flame className="w-12 h-12 text-primary opacity-80" />
          </div>
        </div>

        {/* Info Area */}
        <div className="p-4 flex-1 flex flex-col">
          <h3 className="text-white font-bold text-[15px] mb-2 line-clamp-2">
            {product.name}
          </h3>
          <p className="text-[#9CA3AF] text-[13px] line-clamp-2 mb-3">
            {product.description}
          </p>
          {product.isStandardItem && (
            <div className="mt-auto mb-3">
              <span className="inline-block px-2 py-0.5 rounded-full bg-gray-700 text-gray-300 text-[10px]">
                IS:14609:1999
              </span>
            </div>
          )}

          {/* Action Row - Fades in on hover on desktop, always visible on mobile */}
          <div className="mt-auto flex items-center gap-2 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-300">
            <button
              onClick={() => setSelectedProduct(product)}
              className="flex-1 px-3 py-2 rounded-lg border border-primary text-primary hover:bg-primary hover:text-white transition-colors text-[13px] font-semibold"
            >
              View Details
            </button>
            {hasSpecs && (
              <button
                onClick={() => setSelectedProduct(product)}
                className="w-9 h-9 flex-shrink-0 rounded-lg border border-primary text-primary flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
              >
                <Info className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0F0F1A] pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="mb-8">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl md:text-4xl font-bold font-barlow tracking-tight uppercase"
          >
            <span className="text-white">Product</span> <span className="text-primary">Catalog</span>
          </motion.h1>
        </header>

        {/* Search & Filter Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8 sticky top-[70px] z-30"
        >
          <div className="p-4 rounded-2xl bg-[rgba(255,255,255,0.04)] backdrop-blur-[12px] border border-[rgba(255,255,255,0.08)] shadow-lg">
            <div className="flex flex-col md:flex-row gap-4 items-center">
              
              {/* Search */}
              <div className="flex-1 w-full relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search equipment..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 rounded-xl border border-white/10 bg-[#1A1A2E] text-white placeholder-gray-500 focus:ring-0 focus:border-primary transition-colors outline-none text-sm"
                />
              </div>

              {/* View Toggles & Mobile Dropdown */}
              <div className="flex items-center gap-3 w-full md:w-auto">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="md:hidden flex-1 px-4 py-3 rounded-xl border border-white/10 bg-[#1A1A2E] text-white focus:border-primary outline-none text-sm appearance-none"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>

                <div className="flex bg-[#1A1A2E] rounded-xl border border-white/10 p-1 flex-shrink-0">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'}`}
                  >
                    <Grid className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'}`}
                  >
                    <List className="w-5 h-5" />
                  </button>
                </div>
              </div>

            </div>

            {/* Desktop Category Chips */}
            <div className="hidden md:flex flex-wrap gap-2 mt-4">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`px-4 py-1.5 rounded-full text-[13px] font-semibold transition-colors border ${
                    selectedCategory === cat.value 
                      ? 'bg-primary border-primary text-white' 
                      : 'border-white/10 text-gray-400 hover:text-white hover:border-white/30 bg-[#1A1A2E]'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Results Count */}
        <div className="mb-6 flex justify-between items-center">
          <p className="text-[#888888] text-sm font-medium">
            {loading ? 'Loading...' : `Showing ${filteredAndSortedProducts.length} of ${products.length} products`}
          </p>
        </div>

        {/* Products Grid/List */}
        {loading ? (
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-2xl bg-[#1A1A2E] border border-white/5 overflow-hidden h-[300px] animate-pulse">
                <div className="h-[180px] bg-white/5" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-white/10 rounded w-3/4" />
                  <div className="h-3 bg-white/5 rounded w-full" />
                  <div className="h-3 bg-white/5 rounded w-5/6" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredAndSortedProducts.length > 0 ? (
          <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1'}`}>
            {filteredAndSortedProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Search className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No products found</h3>
            <p className="text-gray-400">Try adjusting your filters or search terms.</p>
          </div>
        )}
      </div>

      {/* Floating Action Button */}
      <a 
        href="#contact" 
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-primary text-white px-5 py-3 rounded-full font-bold shadow-[0_4px_20px_rgba(192,57,43,0.4)] hover:scale-105 hover:bg-primary-600 transition-all duration-300 animate-pulse-subtle"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="hidden sm:inline">Request Quote</span>
      </a>

      {/* Modal is preserved similarly to original but styled */}
      <AnimatePresence>
        {selectedProduct && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedProduct(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-2xl bg-[#1A1A2E] border border-white/10 shadow-2xl"
            >
              <div className="sticky top-0 z-10 p-5 border-b border-white/10 bg-[#1A1A2E]/95 backdrop-blur-md flex justify-between items-start">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">
                    {selectedProduct.name}
                  </h2>
                  <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold bg-primary text-white">
                    {selectedProduct.category}
                  </span>
                </div>
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Image */}
                  <div className="aspect-[4/3] bg-[#111827] rounded-xl overflow-hidden flex items-center justify-center p-4 border border-white/5">
                    {selectedProduct.image ? (
                      <img
                        src={selectedProduct.image}
                        alt={selectedProduct.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[rgba(192,57,43,0.08)] rounded-lg">
                        <Flame className="w-16 h-16 text-primary opacity-80" />
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Description</h4>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        {selectedProduct.description}
                      </p>
                    </div>

                    {selectedProduct.technical_specifications && Object.keys(selectedProduct.technical_specifications).length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Specifications</h4>
                        <div className="space-y-2">
                          {Object.entries(selectedProduct.technical_specifications).map(([key, value]) => (
                            <div key={key} className="flex justify-between py-2 border-b border-white/5 text-sm">
                              <span className="text-gray-400 capitalize">{key.replace(/_/g, ' ')}</span>
                              <span className="text-white font-medium text-right ml-4">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => {
                        setSelectedProduct(null);
                        window.location.href = '/#contact';
                      }}
                      className="w-full py-3 rounded-xl bg-primary hover:bg-primary-600 text-white font-bold transition-colors shadow-glow-red mt-4"
                    >
                      Request Quote
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Catalog;
